import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { batchService } from "/app/src/services";
import { PowerPickTimeZoneToLocal } from "/app/src/components/generic/formatting/dateTime";
import { Search } from "/app/src/components/generic/components/search";
import { useTranslation } from "react-i18next";
import { Batch as BatchType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * A React component that displays and manages batches in a table with search, sorting, and pagination.
 *
 * @returns {JSX.Element} The rendered batches component.
 */
export default function Batches() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<BatchType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: t("translation:name"),
      }),
      columnHelper.accessor("priority", {
        id: "priority",
        cell: (info) => info.getValue(),
        header: t("translation:priority"),
      }),
      columnHelper.accessor("creationDate", {
        id: "creationDate",
        cell: (info) => {
          return (
            <PowerPickTimeZoneToLocal
              date={info.getValue()}
              format={"YYYY-MM-DD HH:mm:ss"}
            />
          );
        },
        header: t("translation:creation_date"),
      }),
    ],
    [columnHelper, t],
  );
  const [sort, setSort] = useSortUpgrade([]);
  const [searchParams] = useSearchParams();
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  /**
   * Navigates to the detailed view of a batch.
   *
   * @param {number} batchId - The ID of the batch.
   */
  const goToBatch = useCallback(
    (batchId: number) => {
      navigate(`/explorer/batches/${batchId}`);
    },
    [navigate],
  );

  const {
    data: batches,
    count: batchesCount,
    isFetching,
  } = usePaginatedData<BatchType>({
    queryKey: ["batches"],
    searchString,
    sort,
    page,
    pageSize,
    service: batchService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: batchesCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:batches")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={batches}
        tableColumns={columns}
        length={batchesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        enableRowSelection
        rowClicked={goToBatch}
        emptyText={t("translation:no_batches")}
      />
    </div>
  );
}
